import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { conditionInfo, inspectionKindLabel, type ChangeDirection } from "@rentorng/core";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { AgencyMark } from "@/components/agency-mark";
import { Copyright } from "@/components/copyright";
import { Badge, Button, Card, ErrorState, PageHeader, Spinner, Table } from "@/components/ui";
import { IconPrinter } from "./Inspections";

/**
 * The inspection report.
 *
 * **A document, not a screen.** It is what a landlord is sent to justify a repair bill and what a
 * tenant is shown when a deposit is not returned in full, so it carries the agency's letterhead,
 * says plainly what was found and what changed, and prints alone on the sheet — the rail, the top
 * bar and the buttons all carry `no-print` and disappear, leaving the `printable` card.
 *
 * Two things it says that a condition list on its own cannot, and both are the reason it exists:
 *
 * - **What changed since the previous inspection, first.** A grade is an opinion; a grade set
 *   against the same line on the previous visit is evidence. The deteriorations lead, because that
 *   is the part somebody is going to argue with.
 * - **Which lines nobody checked.** They print as “not checked” rather than being quietly omitted.
 *   A report that lists only what was seen reads as a clean bill of health for a flat half of which
 *   nobody entered.
 *
 * The figures come from the same endpoint and the same pure functions the review screen uses, so
 * the document and the screen cannot disagree about what the inspection found.
 */

interface Item {
  id: string;
  area: string;
  item: string;
  condition: string | null;
  note: string | null;
  deductionAmount: number | null;
  photos: Array<{ id: string; url: string; name: string | null }>;
}

interface Report {
  inspection: {
    reference: string | null;
    kind: string;
    status: string;
    scheduledFor: string | null;
    completedAt: string | null;
    summary: string | null;
  };
  property: { name: string; city: string | null; addressLine: string | null } | null;
  unit: { label: string } | null;
  tenancy: { reference: string; tenantName: string; startDate: string; endDate: string | null } | null;
  where: string | null;
  items: Item[];
  summary: { total: number; graded: number; findings: number; deductions: number };
  previous: { reference: string | null; completedAt: string | null } | null;
  changes: Array<{ area: string; item: string; from: string | null; to: string | null; direction: ChangeDirection }>;
  comparison: { worse: number; better: number; unchanged: number; unknown: number; added: number; removed: number };
  comparisonSentence: string | null;
  agency: { name: string; phone: string | null; email: string | null; address: string | null; logoUrl: string | null };
  asAt: string;
}

const DIRECTION_WORD: Record<ChangeDirection, string> = {
  worse: "Deteriorated",
  better: "Improved",
  unchanged: "Unchanged",
  unknown: "Not comparable",
  new: "New line",
  gone: "Not inspected this time",
};

export function InspectionReportPage() {
  const { id = "" } = useParams();
  const fmt = useFormat();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["inspection-report", id],
    queryFn: () => api.get<Report>(`/inspections/${id}/report`),
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  const { inspection, summary, agency } = data;
  /* Only what moved. Every line is in the table below; repeating nineteen unchanged rows here would
     bury the two that somebody actually has to answer for. */
  const moved = data.changes.filter((change) => change.direction === "worse" || change.direction === "better" || change.direction === "new");
  const withPhotos = data.items.filter((item) => item.photos.length);

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Inspection report"
        back={{ to: `/app/inspections/${id}`, label: "Inspection" }}
        description="A record of the condition found, and of what has changed since the previous inspection of the same place. It can be sent or printed."
        actions={
          <Button variant="secondary" className="no-print" onClick={() => window.print()}>
            <IconPrinter size={16} /> Print
          </Button>
        }
      />

      <Card padded={false} className="printable">
        {/* ----------------------------------------------------------- letterhead */}
        <div className="border-b border-ink-200 p-6">
          <div className="flex flex-wrap items-start gap-4">
            <AgencyMark name={agency.name} logoUrl={agency.logoUrl}>
              {agency.address ? <p className="text-sm text-ink-500">{agency.address}</p> : null}
              <p className="text-sm text-ink-500">{[agency.phone, agency.email].filter(Boolean).join(" · ")}</p>
            </AgencyMark>
            <div className="ml-auto text-right">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
                {inspectionKindLabel(inspection.kind)} inspection
              </p>
              <p className="font-medium">{inspection.reference}</p>
              <p className="text-sm text-ink-500">as at {data.asAt}</p>
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------------- where */}
        <div className="grid gap-6 border-b border-ink-200 p-6 sm:grid-cols-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Property inspected</p>
            <p className="mt-1 font-medium">{data.where ?? data.property?.name ?? "—"}</p>
            <p className="text-sm text-ink-500">
              {[data.property?.addressLine, data.property?.city].filter(Boolean).join(", ") || "—"}
            </p>
            {!data.unit ? <p className="text-sm text-ink-500">Common parts only.</p> : null}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Tenancy</p>
            <p className="mt-1 font-medium">{data.tenancy?.tenantName ?? "No tenancy in place"}</p>
            <p className="text-sm text-ink-500">
              {data.tenancy
                ? `${data.tenancy.reference} · from ${data.tenancy.startDate}${
                    data.tenancy.endDate ? ` to ${data.tenancy.endDate}` : ""
                  }`
                : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Carried out</p>
            <p className="mt-1 font-medium">
              {inspection.completedAt ? fmt.dateTime(inspection.completedAt) : fmt.date(inspection.scheduledFor)}
            </p>
            <p className="text-sm text-ink-500">
              {inspection.completedAt ? "Completed" : "Not yet completed — this is a draft."}
            </p>
          </div>
        </div>

        {/* ------------------------------------------------------------- figures */}
        <div className="grid gap-4 border-b border-ink-200 p-6 sm:grid-cols-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Items inspected</p>
            <p className="tabular mt-1 text-xl font-semibold">{summary.graded}</p>
            <p className="text-xs text-ink-500">of {summary.total} listed</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Requiring attention</p>
            <p className={`tabular mt-1 text-xl font-semibold ${summary.findings ? "text-danger-600" : ""}`}>
              {summary.findings}
            </p>
            <p className="text-xs text-ink-500">poor, damaged or missing</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Deteriorated</p>
            <p className="tabular mt-1 text-xl font-semibold">{data.previous ? data.comparison.worse : "—"}</p>
            <p className="text-xs text-ink-500">since the previous inspection</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Attributed cost</p>
            <p className="tabular mt-1 text-xl font-semibold">{fmt.money(summary.deductions)}</p>
            <p className="text-xs text-ink-500">across {summary.findings} items</p>
          </div>
        </div>

        {/* ---------------------------------------------------------- the summary */}
        {inspection.summary ? (
          <div className="border-b border-ink-200 p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Inspector's summary</p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink-700">{inspection.summary}</p>
          </div>
        ) : null}

        {/* ------------------------------------------------------- what changed */}
        <div className="border-b border-ink-200 p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Since the previous inspection</p>
          {data.previous ? (
            <>
              <p className="mt-2 text-sm text-ink-700">
                Compared with {data.previous.reference}, carried out {fmt.dateTime(data.previous.completedAt)}.{" "}
                {data.comparisonSentence}
              </p>
              {moved.length ? (
                <div className="mt-3">
                  <Table
                    head={
                      <tr>
                        <th>Where</th>
                        <th>Item</th>
                        <th>Was</th>
                        <th>Now</th>
                        <th>Change</th>
                      </tr>
                    }
                  >
                    {moved.map((change) => (
                      <tr key={`${change.area}|${change.item}`}>
                        <td className="text-ink-500">{change.area}</td>
                        <td className="font-medium">{change.item}</td>
                        <td className="text-ink-600">{change.from ? conditionInfo(change.from).label : "—"}</td>
                        <td className="text-ink-600">{change.to ? conditionInfo(change.to).label : "—"}</td>
                        <td className="text-ink-600">{DIRECTION_WORD[change.direction]}</td>
                      </tr>
                    ))}
                  </Table>
                </div>
              ) : null}
            </>
          ) : (
            <p className="mt-2 text-sm text-ink-700">
              This is the first inspection of this property on record, so there is nothing to compare it with. It is the
              condition every later inspection will be measured against.
            </p>
          )}
        </div>

        {/* ------------------------------------------------------------- the lines */}
        <Table
          head={
            <tr>
              <th>Where</th>
              <th>Item</th>
              <th>Condition</th>
              <th>Note</th>
              <th className="num">Attributed</th>
            </tr>
          }
        >
          {data.items.map((item) => {
            const info = conditionInfo(item.condition);
            return (
              <tr key={item.id}>
                <td className="text-ink-500">{item.area}</td>
                <td className="font-medium">{item.item}</td>
                <td>
                  <Badge tone={info.tone}>{info.label}</Badge>
                </td>
                <td className="text-ink-600">{item.note || "—"}</td>
                <td className="num tabular">{item.deductionAmount ? fmt.money(item.deductionAmount) : "—"}</td>
              </tr>
            );
          })}
        </Table>

        {/* ------------------------------------------------------ the photographs */}
        {withPhotos.length ? (
          <div className="border-t border-ink-200 p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Photographs</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-3">
              {withPhotos.flatMap((item) =>
                item.photos.map((photo) => (
                  <figure key={photo.id}>
                    <img
                      src={photo.url}
                      alt={`${item.area}: ${item.item}`}
                      className="w-full rounded-[var(--radius-control)] border border-ink-200 object-cover"
                    />
                    <figcaption className="mt-1 text-xs text-ink-500">
                      {item.area} · {item.item} — {conditionInfo(item.condition).label.toLowerCase()}
                    </figcaption>
                  </figure>
                )),
              )}
            </div>
          </div>
        ) : null}

        <div className="border-t border-ink-200 p-6 text-xs text-ink-500">
          <p>
            Conditions are recorded on a fixed scale — good, fair, poor, damaged, missing — so that one inspection can be
            compared with the next. A line shown as “not checked” was not seen on this visit and is not a statement
            about its condition.
          </p>
          {summary.deductions > 0 ? (
            <p className="mt-1">
              Amounts shown as attributed are the costs recorded against each item at this inspection. They are not a
              demand, and any deduction from a deposit is dealt with separately.
            </p>
          ) : null}
          <Copyright className="mt-3" />
        </div>
      </Card>
    </div>
  );
}
